import React, { useMemo, useState } from "react";
import {
  Banknote,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Calendar,
  Settings,
  Filter,
  ChevronRight,
  TrendingUp,
} from "lucide-react";

import {
  useDeleteChargePriceMutation,
  useGetChargePricesQuery,
} from "../api/billingApi";

import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";
import ChargePriceModal from "./ChargePriceModal";

export default function ChargePricesPage() {
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [q, setQ] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, refetch, isFetching } = useGetChargePricesQuery({
    year,
  });
  const [del] = useDeleteChargePriceMutation();

  const prices = useMemo(() => data?.data ?? [], [data]);

  // Local filtering for charge names or categories
  const filteredPrices = useMemo(() => {
    if (!q.trim()) return prices;
    const term = q.toLowerCase();
    return prices.filter(
      (x: any) =>
        x.chargeType?.name?.toLowerCase().includes(term) ||
        x.category?.name?.toLowerCase().includes(term) ||
        x.membershipBand?.label?.toLowerCase().includes(term)
    );
  }, [prices, q]);

  const onDelete = async (id: number) => {
    if (
      !window.confirm(
        "CONFIRM PRICE PURGE: This action will remove this pricing point from the current billing cycle."
      )
    )
      return;
    await del(id).unwrap();
    refetch();
  };

  return (
    <PageGate perm={PERMS.billing.priceAccess}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Header Section */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
                <TrendingUp className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                  Price Fee
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium italic">
                  Fee scheduling and annual cycle adjustments.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AccessGate perm={PERMS.billing.priceUpdate}>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
                >
                  <Plus className="w-4 h-4" /> Add Price Point
                </button>
              </AccessGate>
              <button
                onClick={() => refetch()}
                className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 text-indigo-500 ${
                    isFetching ? "animate-spin" : ""
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 2. Configuration Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6">
              <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                <Settings className="w-4 h-4 text-indigo-500" />
                <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                  Controls
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Active Cycle (Year)
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300" />
                    <input
                      type="number"
                      className="w-full pl-5 border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      value={year}
                      onChange={(e) => setYear(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Fee Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300" />
                    <input
                      type="text"
                      placeholder="Charge or Category..."
                      className="w-full pl-5 border-b-2 border-gray-50 py-2 text-[11px] font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Price Matrix Table */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white border-b border-gray-50">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Charge Identity
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Target Group
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Value
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} className="py-20 text-center">
                          <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-4" />
                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                            Loading Matrix...
                          </span>
                        </td>
                      </tr>
                    ) : filteredPrices.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-24 text-center">
                          <Filter className="w-12 h-12 mx-auto text-gray-100 mb-4" />
                          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                            No price entries found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredPrices.map((x: any) => (
                        <tr
                          key={x.id}
                          className="group hover:bg-gray-50/80 transition-all"
                        >
                          <td className="px-8 py-6">
                            <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                              {x.chargeType?.name ?? `#${x.charge_type_id}`}
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest">
                              Fiscal Year: {x.year}
                            </div>
                          </td>
                          <td className="px-8 py-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-500 rounded-lg text-[9px] font-black uppercase tracking-tighter">
                              {x.category?.name ?? "Global Group"}
                              {x.membershipBand?.label && (
                                <>
                                  <ChevronRight className="w-2 h-2" />{" "}
                                  {x.membershipBand.label}
                                </>
                              )}
                            </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <div className="text-lg font-black text-gray-900 tracking-tighter">
                              {new Intl.NumberFormat("en-NG", {
                                style: "currency",
                                currency: "NGN",
                              }).format(x.amount)}
                            </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <AccessGate perm={PERMS.billing.delete}>
                              <button
                                onClick={() => onDelete(x.id)}
                                className="p-2.5 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-rose-600 hover:border-rose-100 transition-all shadow-sm active:scale-90"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AccessGate>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Modal Entry */}
        {isModalOpen && (
          <ChargePriceModal
            defaultYear={year}
            onClose={() => setIsModalOpen(false)}
            onSuccess={() => {
              setIsModalOpen(false);
              refetch();
            }}
          />
        )}
      </div>
    </PageGate>
  );
}
